import React, { useState, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Phone, Mail, Globe } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeamPage = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team`);
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0b2c4a] to-[#1a4b73] text-white py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Meet Our Expert Team
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Our experienced Home Loan Advisors are dedicated to helping you navigate 
              the mortgage process with expertise and personalized service.
            </p>
          </div>
        </div>
      </section>

      {/* Team Members Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {isLoading ? (
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0b2c4a] mx-auto mb-4"></div>
              <p className="text-gray-600">Loading team members...</p>
            </div>
          ) : teamMembers.length === 0 ? (
            <div className="text-center">
              <p className="text-gray-600">No team members found.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {teamMembers.map((member) => (
              <Card key={member.id} className="hover:shadow-lg transition-shadow duration-200 border-none bg-white overflow-hidden">
                <div className="aspect-square overflow-hidden bg-gray-100">
                  {member.photo ? (
                    <img 
                      src={member.photo.startsWith('http') ? member.photo : `${BACKEND_URL}${member.photo}`} 
                      alt={member.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                      onError={(e) => {
                        console.log('Image failed to load:', member.photo);
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `
                          <div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-900 to-blue-700 text-white">
                            <div class="text-center">
                              <div class="text-4xl font-bold mb-2">${member.name.split(' ').map(n => n[0]).join('')}</div>
                              <div class="text-xs opacity-75">Photo Error</div>
                            </div>
                          </div>
                        `;
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0b2c4a] to-[#1a4b73] text-white">
                      <div className="text-center">
                        <div className="text-4xl font-bold mb-2">
                          {member.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div className="text-xs opacity-75">No Photo</div>
                      </div>
                    </div>
                  )}
                </div>
                <CardContent className="p-6">
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      {member.name}
                    </h3>
                    <p className="text-[#0b2c4a] font-medium mb-2">
                      {member.title}
                    </p>
                    <Badge 
                      variant="outline" 
                      className="border-[#545454] text-[#545454] text-xs"
                    >
                      NMLS {member.nmls}
                    </Badge>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                    {member.bio}
                  </p>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center space-x-3 text-sm">
                      <Phone className="h-4 w-4 text-[#0b2c4a] flex-shrink-0" />
                      <span className="text-gray-700">{member.phone}</span>
                    </div>
                    <div className="flex items-center space-x-3 text-sm">
                      <Mail className="h-4 w-4 text-[#0b2c4a] flex-shrink-0" />
                      <span className="text-gray-700">{member.email}</span>
                    </div>
                    <div className="flex items-center space-x-3 text-sm">
                      <Globe className="h-4 w-4 text-[#0b2c4a] flex-shrink-0" />
                      <span className="text-gray-700">{member.website}</span>
                    </div>
                  </div>
                  
                  <Button 
                    className="w-full bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white"
                    onClick={() => window.location.href = `tel:${member.phone}`}
                  >
                    Contact {member.name.split(' ')[0]}
                  </Button>
                </CardContent>
              </Card>
            ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Contact any of our Home Loan Advisors today to discuss your mortgage needs 
            and find the perfect loan program for your situation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white"
              onClick={() => window.location.href = 'tel:(720) 650-7988'}
            >
              Call (720) 650-7988
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-[#0b2c4a] text-[#0b2c4a] hover:bg-[#0b2c4a] hover:text-white"
              asChild
            >
              <a href="/calculators">Use Our Calculators</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TeamPage;